import {CLASSES_STATUS, SUBJECTS_STATUS} from 'common/constants'
import {calculateErrorMessage} from 'common/helpers'
import moment from 'moment'
import {useEffect, useRef, useState} from 'react'
import {useDispatch} from 'react-redux'
import {
  generateStudentClasswiseSummaryReport,
  generateStudentSubjectwiseSummaryReport,
} from 'setup/redux/Actions/Attendance/attendance'
import {getClasses} from 'setup/redux/Actions/Class/class'
import {getGrades} from 'setup/redux/Actions/School/Grade/grade'
import {getSubjects} from 'setup/redux/Actions/Subject/subject'

import {ATTENDANCE_SAMPLE_WINDOW_DAYS} from './attendanceInputSuggestions'

/**
 * Real names pulled from the school so the welcome examples never invent a grade
 * (a college has no "Grade 7"). Undefined until loaded — callers fall back to the
 * grade-free wording rather than showing a placeholder that does not exist here.
 */
export type AttendanceSuggestionSamples = {
  gradeName?: string
  subjectName?: string
}

/**
 * Keeps the summary requests bounded on schools with very large catalogues.
 *
 * Sized to cover a whole real catalogue rather than to be small: the demo college has 283 active
 * classes, and at the previous ceiling of 100 the busiest class in the school sat at position 134
 * — outside the slice, so it could never be picked no matter how the candidates were ranked. A
 * cap that silently drops most of the school is not a safety limit, it is a wrong answer. Classes
 * beyond this are still dropped, so keep it above the largest catalogue we actually serve.
 */
const MAX_SUMMARY_IDS = 500

/** Active records belong to the selected branch when one is picked, like the rest of the portal. */
function scopeToBranch(rows: any[]): any[] {
  const branch = localStorage.getItem('branch')
  const active = rows.filter((item: any) => item?.status === CLASSES_STATUS.ACTIVE)
  if (branch === null) return active
  const scoped = active.filter((item: any) => String(item?.branchId) === branch)
  return scoped.length > 0 ? scoped : active
}

function idsOf(rows: any[]): any[] {
  return rows
    .map((item: any) => item?.id)
    .filter(Boolean)
    .slice(0, MAX_SUMMARY_IDS)
}

function attendanceWindow() {
  return {
    start_date: moment().subtract(ATTENDANCE_SAMPLE_WINDOW_DAYS, 'days').format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD'),
  }
}

/**
 * The complete grade name as a person would say it. A grade named "1" on its own is not a
 * question anyone can answer, so the level is appended ("1 (Cambridge)") unless the grade name
 * already carries it ("A2-Level" under level "A2"). The branch suffix the API puts on the level
 * ("A2 (Main Branch)") is dropped — the branch is already implied by the chat scope.
 */
function completeGradeName(row: any): string | undefined {
  const name = String(row?.name ?? '').trim()
  if (!name) return undefined

  const level = String(row?.levelName ?? '')
    .replace(/\s*\([^)]*\)\s*$/, '')
    .trim()
  if (!level) return name
  if (name.toLowerCase().includes(level.toLowerCase())) return name

  return `${name} (${level})`
}

/** How many students were actually marked on a summary row. Zero means the row proves nothing. */
export function markedCount(row: any): number {
  const total = Number(row?.total_marked || 0)
  if (total > 0) return total
  return (
    Number(row?.total_present || 0) +
    Number(row?.total_absent || 0) +
    Number(row?.total_late || 0) +
    Number(row?.total_leave || 0)
  )
}

/**
 * Summary rows that carry real attendance, minus the grid's "Total" footer row, BUSIEST FIRST.
 *
 * The order is the point. Both chains used to take the first row that happened to match, which
 * is an ordering the API chose for a grid and means nothing here — so the examples named the
 * class with a single mark against one student while a class with eighteen marks across nine sat
 * further down the same response. The question came back technically answered and useless
 * ("the group consists of 2 students, but their attendance statuses are unmarked").
 * Sorting once here is what makes `[0]` the best candidate for every caller.
 */
export function markedRows(res: any): any[] {
  const rows: any[] = res?.result?.data || res?.result || []
  return rows
    .filter((row) => !row?.isTotal && markedCount(row) > 0)
    .sort((a, b) => markedCount(b) - markedCount(a))
}

/**
 * WHAT THE EXAMPLES SAID LAST TIME, remembered per branch.
 *
 * The names take three round trips to settle — grades, then classes, then the attendance
 * summary that says which grade actually has marks — so every open of the chat showed the
 * grade-free fallback first and rewrote itself a second or two later, in front of the reader.
 * The text they were mid-way through reading changed under them.
 *
 * The answer is not a faster lookup, it is not doing the lookup in front of them: the last
 * settled names are seeded synchronously into the first render, and the refresh happens behind
 * that. Only the first ever open of a browser has nothing to show, and the prefetch below covers
 * most of those too.
 *
 * Grade and subject names are not sensitive and the store is per browser, per origin — this is
 * the same class of thing as the "welcome seen" flag beside it.
 */
const CACHE_KEY = 'chatbot-attendance-suggestion-samples'

function cacheKeyForBranch(): string {
  const branch = localStorage.getItem('branch')
  return branch === null ? CACHE_KEY : `${CACHE_KEY}:${branch}`
}

/** Never throws: private mode, cleared site data and blocked storage all read as "no cache". */
function readCachedSamples(): AttendanceSuggestionSamples {
  try {
    const raw = localStorage.getItem(cacheKeyForBranch())
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return {}
    const gradeName = typeof parsed.gradeName === 'string' ? parsed.gradeName : undefined
    const subjectName = typeof parsed.subjectName === 'string' ? parsed.subjectName : undefined
    return {...(gradeName ? {gradeName} : {}), ...(subjectName ? {subjectName} : {})}
  } catch {
    return {}
  }
}

function writeCachedSamples(samples: AttendanceSuggestionSamples): void {
  try {
    localStorage.setItem(cacheKeyForBranch(), JSON.stringify(samples))
  } catch {
    /* storage unavailable — the examples still work, they just start generic next time */
  }
}

/**
 * How long to leave the school's own page alone before warming the examples.
 *
 * The lookups run while the chat is merely MOUNTED, not only once it is opened, so that the
 * first open has something real to show. That is five requests a user who never opens the chat
 * did not ask for, so they wait until the page they DID ask for has settled.
 */
export const PREFETCH_DELAY_MS = 1500

/** The prefetch, and one retry when the chat is opened with nothing to show. Never more. */
const MAX_ATTEMPTS = 2

export function useAttendanceSuggestionSamples(
  enabled: boolean,
  /** True while the chat is OPEN: look the names up now rather than waiting for the page to settle. */
  immediate: boolean = true
): AttendanceSuggestionSamples {
  const dispatch = useDispatch()
  // Seeded from the last settled answer, so the FIRST paint already says the right thing.
  const [samples, setSamples] = useState<AttendanceSuggestionSamples>(readCachedSamples)
  // What was on screen when this mount began. A PROVISIONAL name — the first active grade,
  // before anything is known about whether it has attendance — must never displace it: that swap
  // IS the flicker, and it replaces a good name with a worse one on its way back to a good one.
  const seededRef = useRef<AttendanceSuggestionSamples>(samples)
  const startedRef = useRef(false)
  const cancelledRef = useRef(false)
  // Whether a lookup has actually PRODUCED a name. The prefetch runs while the chat is closed,
  // which can be early enough in the app's own start-up for the requests to come back with
  // nothing — and a run that answered nothing must not count as the one run we were allowed.
  // Opening the chat retries; the flag is what keeps that from being an open-a-tab-fire-five-
  // requests loop.
  const settledRef = useRef<{[K in keyof AttendanceSuggestionSamples]?: boolean}>({})
  const attemptsRef = useRef(0)

  useEffect(() => {
    // Cleared on the way IN as well as set on the way out: a StrictMode remount reuses these
    // refs, and a flag left true would silently discard every callback of the run in flight.
    cancelledRef.current = false
    return () => {
      cancelledRef.current = true
    }
  }, [])

  useEffect(() => {
    if (!enabled) return
    // At most one lookup per mount, plus ONE retry when the chat is opened and the first one
    // came back with nothing. Without the retry a prefetch that lands too early in the app's
    // start-up leaves the examples on their grade-free wording for the whole session; without
    // the ceiling, every open would fire another five requests.
    // BOTH halves, not either. A school whose subjects answered and whose grades did not still
    // has generic examples, and that half-answer used to count as a run that worked.
    const complete = Boolean(settledRef.current.gradeName && settledRef.current.subjectName)
    const retrying = immediate && !complete && attemptsRef.current < MAX_ATTEMPTS
    if (startedRef.current && !retrying) return

    const cancelled = () => cancelledRef.current

    // The classes of the grade the examples ended up naming — AWAITED by the subject chain,
    // never merely read by it.
    //
    // The subject example names the grade too ("grade 1 (General)'s attendance in Physics"), so
    // the subject has to be one of THAT grade's. A shared variable could not do that: the grade
    // chain is three requests deep (grades, classes, classwise summary) and the subject chain is
    // two, so the subject was chosen while this was still null on essentially every load and the
    // fallback took the busiest marked subject in the SCHOOL. That is how a school teaching
    // Physics only in 1 A (Primary) was offered "grade 1 (General)'s attendance in Physics".
    //
    // Resolving with null means "no grade to pair with": the subject example then keeps its
    // grade-free wording rather than naming a subject that grade was never checked against.
    let settleGradeClasses: (names: Set<string> | null) => void = () => {}
    const gradeClasses = new Promise<Set<string> | null>((resolve) => {
      // Resolve once. Every bail-out in the grade chain calls this, so the subject chain always
      // finishes deciding instead of waiting on a chain that gave up.
      let done = false
      settleGradeClasses = (names) => {
        if (done) return
        done = true
        resolve(names)
      }
    })

    // The names THIS run verified, which is what gets cached — not the names on screen.
    // Merging into whatever was displayed wrote the settled grade beside a subject left over
    // from an earlier run, and that mismatched pair was then seeded into the next open.
    const settledThisRun: AttendanceSuggestionSamples = {}

    const setSample = (
      key: keyof AttendanceSuggestionSamples,
      value?: string,
      {provisional = false}: {provisional?: boolean} = {}
    ) => {
      if (cancelled() || !value) return
      if (provisional && seededRef.current[key]) return
      if (!provisional) {
        settledRef.current[key] = true
        settledThisRun[key] = value
      }
      setSamples((prev) => {
        const next = {...prev, [key]: value}
        // Only SETTLED names — checked against real attendance, and against each other — are
        // worth showing first next time.
        if (!provisional) writeCachedSamples(settledThisRun)
        return next
      })
    }

    const loadSamples = () => {
      // Grades: show the first active one straight away, then upgrade to one that actually has
      // attendance marked so the example question returns an answer rather than an empty report.
      dispatch(
        getGrades(
          {search: {status: CLASSES_STATUS.ACTIVE}, sort: {name: 'ASC'}},
          (gradesRes: any) => {
            if (cancelled()) return settleGradeClasses(null)
            const grades = scopeToBranch(gradesRes?.result?.data || [])
            if (grades.length === 0) return settleGradeClasses(null)

            setSample('gradeName', completeGradeName(grades[0]), {provisional: true})

            dispatch(
              getClasses(
                {search: {status: CLASSES_STATUS.ACTIVE}},
                (classesRes: any) => {
                  if (cancelled()) return settleGradeClasses(null)
                  const classes = scopeToBranch(classesRes?.result?.data || [])
                  const classIds = idsOf(classes)
                  if (classIds.length === 0) return settleGradeClasses(null)

                  dispatch(
                    generateStudentClasswiseSummaryReport(
                      {classIds, ...attendanceWindow()},
                      (summaryRes: any) => {
                        if (cancelled()) return settleGradeClasses(null)
                        const marked = markedRows(summaryRes)
                        if (marked.length === 0) return settleGradeClasses(null)

                        // Summary rows carry the class name ("1-A"); map it back to the grade it
                        // belongs to so the example names a grade, not a section. Walked
                        // busiest-first and stopping at the first row that resolves, so the
                        // example names the class with the most attendance behind it — not
                        // whichever one the summary happened to list first. A row whose class is
                        // missing from the catalogue (renamed, or outside the branch scope)
                        // yields to the next best rather than ending the search.
                        let grade: any
                        for (const row of marked) {
                          const className = String(row?.class_name ?? '').trim()
                          if (!className) continue
                          const markedClass = classes.find(
                            (item: any) => String(item?.name ?? '').trim() === className
                          )
                          if (!markedClass) continue
                          grade = grades.find(
                            (item: any) => String(item?.id) === String(markedClass?.gradeId)
                          )
                          if (grade) break
                        }
                        if (!grade) return settleGradeClasses(null)

                        setSample('gradeName', completeGradeName(grade))
                        settleGradeClasses(
                          new Set(
                            classes
                              .filter((item: any) => String(item?.gradeId) === String(grade?.id))
                              .map((item: any) => String(item?.name ?? '').trim())
                              .filter(Boolean)
                          )
                        )
                      },
                      (err: any) => {
                        settleGradeClasses(null)
                        calculateErrorMessage(err)
                      }
                    )
                  )
                },
                (err: any) => {
                  settleGradeClasses(null)
                  calculateErrorMessage(err)
                }
              )
            )
          },
          (err: any) => {
            settleGradeClasses(null)
            calculateErrorMessage(err)
          }
        )
      )

      // Subjects: the summary is fetched in parallel, but the CHOICE waits for the grade's
      // classes. No provisional subject is shown — the first active subject alphabetically has
      // been checked against nothing, and a subject is only ever shown beside a grade, so an
      // unverified one is a wrong pair on screen. Until the real one lands the example keeps
      // its grade-free wording, which is the file's rule everywhere else: no guessing.
      dispatch(
        getSubjects(
          {search: {status: SUBJECTS_STATUS.ACTIVE}, sort: {name: 'ASC'}},
          (subjectsRes: any) => {
            if (cancelled()) return
            const subjects: any[] = subjectsRes?.result?.data || []
            if (subjects.length === 0) return

            const subjectIds = idsOf(subjects)
            if (subjectIds.length === 0) return

            dispatch(
              generateStudentSubjectwiseSummaryReport(
                {subjectIds, ...attendanceWindow()},
                (summaryRes: any) => {
                  if (cancelled()) return
                  const marked = markedRows(summaryRes)
                  if (marked.length === 0) return

                  // GRADE FIRST, THEN ITS SUBJECT — never the other way round, and never
                  // school-wide. The row has to belong to a class of the grade the other
                  // examples name, so grade -> class -> subject is a chain the data itself
                  // proves. Busiest-first, so `[0]` is the subject with the most attendance
                  // behind it rather than the first listed.
                  gradeClasses.then((classNames) => {
                    if (cancelled() || !classNames) return
                    const inGrade = marked.filter((row) =>
                      classNames.has(String(row?.class_name ?? '').trim())
                    )
                    // Nothing marked for this grade: say nothing about subjects. Naming one
                    // from another grade is the bug this replaced — the assistant then answers
                    // about a subject the grade does not take.
                    if (inGrade.length === 0) return
                    setSample('subjectName', String(inGrade[0]?.subject_name ?? '').trim())
                  })
                },
                (err: any) => calculateErrorMessage(err)
              )
            )
          },
          (err: any) => calculateErrorMessage(err)
        )
      )
    }

    const run = () => {
      startedRef.current = true
      attemptsRef.current += 1
      loadSamples()
    }

    if (immediate) {
      run()
      return
    }

    // MERELY MOUNTED. The chat sits on every page whether or not anybody opens it, so warming
    // the examples costs five requests a user did not ask for — they wait until the page they
    // DID ask for has settled.
    const timer = window.setTimeout(() => {
      if (cancelledRef.current || startedRef.current) return
      run()
    }, PREFETCH_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [dispatch, enabled, immediate])

  return samples
}
